import { useState, useEffect } from "react";
import { User, LogOut, ShoppingBag, Plus, MessageCircle, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import type { User as SupabaseUser } from "@supabase/supabase-js";

interface UserMenuProps {
  onAuthClick: () => void;
  onPurchasesClick: () => void;
  onAddBeatClick: () => void;
  onFeaturedBeatsClick?: () => void;
}

export const UserMenu = ({ onAuthClick, onPurchasesClick, onAddBeatClick, onFeaturedBeatsClick }: UserMenuProps) => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const { isAdmin } = useUserRole();
  const { toast } = useToast();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Signed out successfully",
      });
    }
  };

  if (!user) {
    return (
      <Button onClick={onAuthClick} variant="outline">
        Sign In
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon">
          <User className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onPurchasesClick}>
          <ShoppingBag className="mr-2 h-4 w-4" />
          My Purchases
        </DropdownMenuItem>
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onAddBeatClick}>
              <Plus className="mr-2 h-4 w-4" />
              Add Beat
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onFeaturedBeatsClick}>
              <Star className="mr-2 h-4 w-4" />
              Featured Beats
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};